import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Info, Buildings, Certificate, ShieldCheck, Database, Robot } from '@phosphor-icons/react';
import { API } from '@/App';

// Organization capacity options (Phase 1)
const ORGANIZATION_CAPACITIES = [
  { id: 'bank', label: 'Bank / Mutual Bank', description: 'Licensed banking institution under the Banks Act' },
  { id: 'insurer_long', label: 'Insurer (Long-term)', description: 'Life insurance, pension-backed policies' },
  { id: 'insurer_short', label: 'Insurer (Short-term)', description: 'Non-life insurance (motor, property, etc.)' },
  { id: 'investment_manager', label: 'Investment Manager / Discretionary FSP', description: 'Manages investments on behalf of clients' },
  { id: 'financial_advisor', label: 'Financial Advisor / Intermediary', description: 'Provides financial advice or intermediary services' },
  { id: 'cis_manager', label: 'Collective Investment Scheme (CIS) Manager', description: 'Manages unit trusts or collective investments' },
  { id: 'pension_fund', label: 'Retirement / Pension Fund', description: 'Administers retirement or pension funds' },
  { id: 'casp_vasp', label: 'Crypto Asset Service Provider (CASP/VASP)', description: 'Crypto exchanges, wallets, or virtual asset services' },
  { id: 'mobile_payment', label: 'Mobile Payment Provider', description: 'Mobile money or payment services' }
];

// FSP Categories (Phase 2)
const FSP_CATEGORIES = [
  { id: 'cat_i', label: 'Category I', description: 'Advice and intermediary services (excluding discretionary)' },
  { id: 'cat_ii', label: 'Category II', description: 'Discretionary FSP' },
  { id: 'cat_iia', label: 'Category IIA', description: 'Hedge Fund FSP' },
  { id: 'cat_iii', label: 'Category III', description: 'Administrative FSP' },
  { id: 'cat_iv', label: 'Category IV', description: 'Assistance Business FSP' }
];

// Accountable Institution tooltip content
const ACCOUNTABLE_INSTITUTION_TOOLTIP = `Schedule 1 of the FIC Act lists "Accountable Institutions" including:
• Banks and Mutual Banks
• Life Insurers
• Financial Services Providers (FSPs)
• Attorneys and Trust Companies
• Estate Agents
• Crypto Asset Service Providers (CASPs)
• Motor Vehicle Dealers (high-value)
• Dealers in Precious Metals/Stones

If you're unsure, you can check Schedule 1 of the Financial Intelligence Centre Act (FICA).`;

const IntakeWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Phase 1: Basic Company Profile
    company_name: '',
    registration_number: '',
    organization_capacities: [],
    
    // Phase 2: Licensing & Regulatory Status
    is_fsp: null, // Yes/No
    fsp_categories: [],
    takes_deposits: null, // Yes/No - Triggers Banks Act
    manages_client_assets: null, // Yes/No - Triggers Financial Institutions (Protection of Funds) Act
    
    // Phase 3: Financial Crime & AML
    is_accountable_institution: null, // Yes/No/Unsure
    facilitates_fund_transfers: null, // Yes/No - Triggers FIC Reporting
    is_designated_fi_fatca_crs: null, // Yes/No - Triggers FATCA/CRS
    
    // Phase 4: Data & Privacy
    is_responsible_party: null, // Yes (Controller) / No (Processor)
    transfers_data_outside_sa: null, // Yes/No - Triggers POPIA Section 72
    
    // Phase 5: Technology & AI
    utilizes_ai: null, // Yes/No
    develops_ai: null // Yes/No - Triggers SA AI Policy & EU AI Act
  });

  const steps = [
    { title: 'Company Profile', subtitle: 'Basic entity information', icon: <Buildings size={20} /> },
    { title: 'Licensing & Regulatory', subtitle: 'FSP and prudential status', icon: <Certificate size={20} /> },
    { title: 'Financial Crime & AML', subtitle: 'FICA & FATCA/CRS obligations', icon: <ShieldCheck size={20} /> },
    { title: 'Data & Privacy', subtitle: 'POPIA compliance', icon: <Database size={20} /> },
    { title: 'Technology & AI', subtitle: 'AI governance requirements', icon: <Robot size={20} /> }
  ];

  const handleCapacityToggle = (capacityId) => {
    setFormData(prev => ({
      ...prev,
      organization_capacities: prev.organization_capacities.includes(capacityId)
        ? prev.organization_capacities.filter(c => c !== capacityId)
        : [...prev.organization_capacities, capacityId]
    }));
  };

  const handleFspCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      fsp_categories: prev.fsp_categories.includes(categoryId)
        ? prev.fsp_categories.filter(c => c !== categoryId)
        : [...prev.fsp_categories, categoryId]
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.company_name.trim()) {
      toast.error('Please enter your company name');
      return;
    }
    if (formData.organization_capacities.length === 0) {
      toast.error('Please select at least one organization capacity');
      return;
    }

    setLoading(true);
    try {
      const intakeResponse = await axios.post(`${API}/intake/submit`, formData);
      const intakeId = intakeResponse.data.id;
      toast.success('Intake submitted! Generating your Regulatory Universe...');
      navigate('/navi-intro', { state: { intakeId } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit intake');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.company_name.trim() && formData.organization_capacities.length > 0;
      case 1:
        return formData.is_fsp !== null;
      case 2:
        return formData.is_accountable_institution !== null;
      case 3:
        return formData.is_responsible_party !== null;
      case 4:
        return formData.utilizes_ai !== null;
      default:
        return true;
    }
  };

  // Phase 1: Basic Company Profile
  const renderPhase1 = () => (
    <div className="space-y-8">
      {/* Company Name */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Company Registered Name *
        </label>
        <input
          type="text"
          value={formData.company_name}
          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
          placeholder="Enter your company's registered name"
          className="w-full px-4 py-3 bg-background-surface border border-border rounded-sm text-text-primary placeholder-text-tertiary focus:border-gold focus:outline-none"
          data-testid="company-name-input"
        />
      </div>

      {/* Registration Number */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Registration Number
        </label>
        <input
          type="text"
          value={formData.registration_number}
          onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
          placeholder="e.g., 2020/123456/07"
          className="w-full px-4 py-3 bg-background-surface border border-border rounded-sm text-text-primary placeholder-text-tertiary focus:border-gold focus:outline-none"
          data-testid="registration-number-input"
        />
      </div>

      {/* Organization Capacity (Multi-select) */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          In which capacity does your organization primarily operate? *
        </label>
        <p className="text-xs text-text-tertiary mb-4">Select all that apply</p>
        <div className="grid grid-cols-1 gap-3">
          {ORGANIZATION_CAPACITIES.map((capacity) => (
            <button
              key={capacity.id}
              onClick={() => handleCapacityToggle(capacity.id)}
              className={`p-4 rounded-sm border text-left transition-all ${
                formData.organization_capacities.includes(capacity.id)
                  ? 'border-gold bg-gold/10'
                  : 'border-border bg-background-surface hover:border-gold/50'
              }`}
              data-testid={`capacity-${capacity.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-sm border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  formData.organization_capacities.includes(capacity.id)
                    ? 'bg-gold border-gold'
                    : 'border-border'
                }`}>
                  {formData.organization_capacities.includes(capacity.id) && (
                    <Check size={14} weight="bold" className="text-gold-foreground" />
                  )}
                </div>
                <div>
                  <div className="font-medium text-text-primary">{capacity.label}</div>
                  <div className="text-xs text-text-tertiary mt-1">{capacity.description}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // Phase 2: Licensing & Regulatory Status
  const renderPhase2 = () => (
    <div className="space-y-8">
      {/* FSP Question */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-4">
          Are you an authorized Financial Services Provider (FSP) under the FAIS Act? *
        </label>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, is_fsp: true })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.is_fsp === true
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="fsp-yes"
          >
            <div className="font-medium text-text-primary">Yes</div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, is_fsp: false, fsp_categories: [] })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.is_fsp === false
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="fsp-no"
          >
            <div className="font-medium text-text-primary">No</div>
          </button>
        </div>
      </div>

      {/* FSP Categories (Conditional) */}
      {formData.is_fsp === true && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Please specify your FSP Category(ies)
          </label>
          <p className="text-xs text-text-tertiary mb-4">Select all that apply</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FSP_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleFspCategoryToggle(cat.id)}
                className={`p-3 rounded-sm border text-left transition-all ${
                  formData.fsp_categories.includes(cat.id)
                    ? 'border-gold bg-gold/10'
                    : 'border-border bg-background-surface hover:border-gold/50'
                }`}
                data-testid={`fsp-${cat.id}`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                    formData.fsp_categories.includes(cat.id)
                      ? 'bg-gold border-gold'
                      : 'border-border'
                  }`}>
                    {formData.fsp_categories.includes(cat.id) && (
                      <Check size={12} weight="bold" className="text-gold-foreground" />
                    )}
                  </div>
                  <div>
                    <span className="font-medium text-text-primary">{cat.label}</span>
                    <span className="text-xs text-text-tertiary ml-2">- {cat.description}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Takes Deposits */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Do you take deposits from the public?
        </label>
        <p className="text-xs text-text-tertiary mb-4">Triggers the Banks Act compliance requirements</p>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, takes_deposits: true })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.takes_deposits === true
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="deposits-yes"
          >
            <div className="font-medium text-text-primary">Yes</div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, takes_deposits: false })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.takes_deposits === false
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="deposits-no"
          >
            <div className="font-medium text-text-primary">No</div>
          </button>
        </div>
      </div>

      {/* Manages Client Assets */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Do you manage or hold "client assets" or "trust property"?
        </label>
        <p className="text-xs text-text-tertiary mb-4">Triggers the Financial Institutions (Protection of Funds) Act</p>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, manages_client_assets: true })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.manages_client_assets === true
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="assets-yes"
          >
            <div className="font-medium text-text-primary">Yes</div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, manages_client_assets: false })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.manages_client_assets === false
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="assets-no"
          >
            <div className="font-medium text-text-primary">No</div>
          </button>
        </div>
      </div>
    </div>
  );

  // Phase 3: Financial Crime & AML
  const renderPhase3 = () => (
    <div className="space-y-8">
      {/* Accountable Institution */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-text-secondary">
            Are you listed as an "Accountable Institution" under Schedule 1 of the FIC Act? *
          </label>
          <div className="relative group">
            <Info size={16} className="text-gold cursor-help" />
            <div className="absolute left-0 bottom-full mb-2 w-80 p-3 bg-background-surface border border-gold rounded-sm text-xs text-text-secondary opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 whitespace-pre-line">
              {ACCOUNTABLE_INSTITUTION_TOOLTIP}
            </div>
          </div>
        </div>
        <p className="text-xs text-text-tertiary mb-4">
          Includes FSPs, Banks, Life Insurers, CASPs, and certain high-value goods dealers
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, is_accountable_institution: 'yes' })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.is_accountable_institution === 'yes'
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="accountable-yes"
          >
            <div className="font-medium text-text-primary">Yes</div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, is_accountable_institution: 'no' })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.is_accountable_institution === 'no'
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="accountable-no"
          >
            <div className="font-medium text-text-primary">No</div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, is_accountable_institution: 'unsure' })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.is_accountable_institution === 'unsure'
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="accountable-unsure"
          >
            <div className="font-medium text-text-primary">Unsure</div>
          </button>
        </div>
      </div>

      {/* Facilitates Fund Transfers */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Do you facilitate the transfer of funds or value for clients (Cross-border or domestic)?
        </label>
        <p className="text-xs text-text-tertiary mb-4">
          Triggers specific FIC Reporting and Suspicious Activity Report (SAR) obligations
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, facilitates_fund_transfers: true })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.facilitates_fund_transfers === true
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="transfers-yes"
          >
            <div className="font-medium text-text-primary">Yes</div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, facilitates_fund_transfers: false })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.facilitates_fund_transfers === false
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="transfers-no"
          >
            <div className="font-medium text-text-primary">No</div>
          </button>
        </div>
      </div>

      {/* FATCA/CRS Designated Financial Institution */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-text-secondary">
            Is your organisation a Designated Financial Institution in terms of FATCA/CRS?
          </label>
          <div className="relative group">
            <Info size={16} className="text-gold cursor-help" />
            <div className="absolute left-0 bottom-full mb-2 w-96 p-3 bg-background-surface border border-gold rounded-sm text-xs text-text-secondary opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <p className="font-medium text-gold mb-2">Designated Financial Institution (DFI):</p>
              <p className="mb-2">Under FATCA (Foreign Account Tax Compliance Act) and CRS (Common Reporting Standard), a DFI includes:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Depository institutions (banks)</li>
                <li>Custodial institutions</li>
                <li>Investment entities (funds, asset managers)</li>
                <li>Specified insurance companies (cash value policies)</li>
              </ul>
              <p className="mt-2 text-gold">If designated, you must report account holder information to SARS for exchange with foreign tax authorities.</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-text-tertiary mb-4">
          Triggers FATCA and CRS reporting obligations to SARS
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, is_designated_fi_fatca_crs: true })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.is_designated_fi_fatca_crs === true
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="fatca-yes"
          >
            <div className="font-medium text-text-primary">Yes</div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, is_designated_fi_fatca_crs: false })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.is_designated_fi_fatca_crs === false
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="fatca-no"
          >
            <div className="font-medium text-text-primary">No</div>
          </button>
        </div>
      </div>
    </div>
  );

  // Phase 4: Data & Privacy
  const renderPhase4 = () => (
    <div className="space-y-8">
      {/* Responsible Party / Controller */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          In your operations, do you determine the "purpose and means" of processing personal information? *
        </label>
        <p className="text-xs text-text-tertiary mb-4">
          This determines your role under the Protection of Personal Information Act (POPIA)
        </p>
        <div className="grid grid-cols-1 gap-3">
          <button
            onClick={() => setFormData({ ...formData, is_responsible_party: true })}
            className={`p-4 rounded-sm border text-left transition-all ${
              formData.is_responsible_party === true
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="controller-yes"
          >
            <div className="font-medium text-text-primary">Yes - We are a Responsible Party (Controller)</div>
            <div className="text-xs text-text-tertiary mt-1">
              You decide what personal data to collect, why, and how it's processed
            </div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, is_responsible_party: false })}
            className={`p-4 rounded-sm border text-left transition-all ${
              formData.is_responsible_party === false
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="controller-no"
          >
            <div className="font-medium text-text-primary">No - We are an Operator (Processor)</div>
            <div className="text-xs text-text-tertiary mt-1">
              You process data only on behalf of another entity who determines the purpose
            </div>
          </button>
        </div>
      </div>

      {/* Cross-border Data Transfers */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Do you transfer personal information of South African data subjects outside of the Republic?
        </label>
        <p className="text-xs text-text-tertiary mb-4">
          Triggers Section 72 of POPIA (Cross-border transfers) compliance requirements
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, transfers_data_outside_sa: true })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.transfers_data_outside_sa === true
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="data-transfer-yes"
          >
            <div className="font-medium text-text-primary">Yes</div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, transfers_data_outside_sa: false })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.transfers_data_outside_sa === false
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="data-transfer-no"
          >
            <div className="font-medium text-text-primary">No</div>
          </button>
        </div>
      </div>

      {/* Summary Preview */}
      <div className="mt-8 p-6 bg-background-surface border border-gold/30 rounded-sm">
        <h3 className="text-sm font-medium text-gold mb-4">Regulatory Universe Preview</h3>
        <p className="text-xs text-text-secondary mb-3">
          Based on your responses, your universe will include regulations for:
        </p>
        <div className="flex flex-wrap gap-2">
          {/* Always include core */}
          <span className="px-2 py-1 bg-background rounded text-xs text-text-primary border border-border">
            FSR Act (Twin Peaks)
          </span>
          <span className="px-2 py-1 bg-background rounded text-xs text-text-primary border border-border">
            Companies Act
          </span>
          
          {/* Based on capacity */}
          {formData.organization_capacities.includes('bank') && (
            <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
              Banks Act
            </span>
          )}
          {(formData.organization_capacities.includes('insurer_long') || formData.organization_capacities.includes('insurer_short')) && (
            <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
              Insurance Act
            </span>
          )}
          {formData.organization_capacities.includes('pension_fund') && (
            <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
              Pension Funds Act
            </span>
          )}
          {formData.organization_capacities.includes('casp_vasp') && (
            <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
              CASP Regulations
            </span>
          )}
          
          {/* Based on FSP */}
          {formData.is_fsp && (
            <>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                FAIS Act
              </span>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                General Code of Conduct
              </span>
            </>
          )}
          
          {/* Based on accountable institution */}
          {(formData.is_accountable_institution === 'yes' || formData.is_accountable_institution === 'unsure') && (
            <>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                FIC Act (FICA)
              </span>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                AML/CFT Regulations
              </span>
            </>
          )}
          
          {/* Based on data controller */}
          {formData.is_responsible_party !== null && (
            <>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                POPIA
              </span>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                PAIA
              </span>
            </>
          )}
          
          {/* Cross-border */}
          {formData.transfers_data_outside_sa && (
            <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
              POPIA Section 72
            </span>
          )}
          
          {/* FATCA/CRS */}
          {formData.is_designated_fi_fatca_crs && (
            <>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                FATCA
              </span>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                CRS
              </span>
            </>
          )}
          
          {/* AI Regulations */}
          {(formData.utilizes_ai || formData.develops_ai) && (
            <>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                SA AI Policy
              </span>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                EU AI Act
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Phase 5: Technology & AI
  const renderPhase5 = () => (
    <div className="space-y-8">
      {/* AI Usage */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Does your organisation utilise AI (Artificial Intelligence)? *
        </label>
        <p className="text-xs text-text-tertiary mb-4">
          This includes using AI tools, machine learning models, chatbots, automated decision-making systems, or AI-powered analytics
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, utilizes_ai: true })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.utilizes_ai === true
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="ai-use-yes"
          >
            <div className="font-medium text-text-primary">Yes</div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, utilizes_ai: false })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.utilizes_ai === false
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="ai-use-no"
          >
            <div className="font-medium text-text-primary">No</div>
          </button>
        </div>
      </div>

      {/* AI Development */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Does your organisation develop AI solutions?
        </label>
        <p className="text-xs text-text-tertiary mb-4">
          This includes building, training, or deploying AI models for internal use or for clients
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => setFormData({ ...formData, develops_ai: true })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.develops_ai === true
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="ai-dev-yes"
          >
            <div className="font-medium text-text-primary">Yes</div>
          </button>
          <button
            onClick={() => setFormData({ ...formData, develops_ai: false })}
            className={`flex-1 p-4 rounded-sm border transition-all ${
              formData.develops_ai === false
                ? 'border-gold bg-gold/10'
                : 'border-border bg-background-surface hover:border-gold/50'
            }`}
            data-testid="ai-dev-no"
          >
            <div className="font-medium text-text-primary">No</div>
          </button>
        </div>
      </div>

      {/* AI Governance Info Box */}
      {(formData.utilizes_ai || formData.develops_ai) && (
        <div className="p-4 bg-gold/10 border border-gold/30 rounded-sm">
          <div className="flex items-start gap-3">
            <Robot size={20} className="text-gold flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-text-primary font-medium mb-2">AI Governance Requirements Apply</p>
              <p className="text-xs text-text-secondary">
                Based on your responses, your Regulatory Universe will include:
              </p>
              <ul className="mt-2 space-y-1 text-xs text-text-secondary">
                <li className="flex items-center gap-2">
                  <Check size={14} className="text-gold" />
                  South Africa's National AI Policy Framework
                </li>
                <li className="flex items-center gap-2">
                  <Check size={14} className="text-gold" />
                  EU AI Act Requirements (for international operations or best practice)
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Summary Preview */}
      <div className="mt-8 p-6 bg-background-surface border border-gold/30 rounded-sm">
        <h3 className="text-sm font-medium text-gold mb-4">Regulatory Universe Preview</h3>
        <p className="text-xs text-text-secondary mb-3">
          Based on your responses, your universe will include regulations for:
        </p>
        <div className="flex flex-wrap gap-2">
          {/* Always include core */}
          <span className="px-2 py-1 bg-background rounded text-xs text-text-primary border border-border">
            FSR Act (Twin Peaks)
          </span>
          <span className="px-2 py-1 bg-background rounded text-xs text-text-primary border border-border">
            Companies Act
          </span>
          
          {/* Based on capacity */}
          {formData.organization_capacities.includes('bank') && (
            <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
              Banks Act
            </span>
          )}
          {(formData.organization_capacities.includes('insurer_long') || formData.organization_capacities.includes('insurer_short')) && (
            <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
              Insurance Act
            </span>
          )}
          {formData.organization_capacities.includes('pension_fund') && (
            <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
              Pension Funds Act
            </span>
          )}
          {formData.organization_capacities.includes('casp_vasp') && (
            <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
              CASP Regulations
            </span>
          )}
          
          {/* Based on FSP */}
          {formData.is_fsp && (
            <>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                FAIS Act
              </span>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                General Code of Conduct
              </span>
            </>
          )}
          
          {/* Based on accountable institution */}
          {(formData.is_accountable_institution === 'yes' || formData.is_accountable_institution === 'unsure') && (
            <>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                FIC Act (FICA)
              </span>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                AML/CFT Regulations
              </span>
            </>
          )}
          
          {/* FATCA/CRS */}
          {formData.is_designated_fi_fatca_crs && (
            <>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                FATCA
              </span>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                CRS
              </span>
            </>
          )}
          
          {/* Data protection */}
          {formData.is_responsible_party !== null && (
            <>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                POPIA
              </span>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                PAIA
              </span>
            </>
          )}
          
          {/* Cross-border */}
          {formData.transfers_data_outside_sa && (
            <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
              POPIA Section 72
            </span>
          )}
          
          {/* AI Regulations */}
          {(formData.utilizes_ai || formData.develops_ai) && (
            <>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                SA AI Policy
              </span>
              <span className="px-2 py-1 bg-gold/20 rounded text-xs text-gold border border-gold/50">
                EU AI Act
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0: return renderPhase1();
      case 1: return renderPhase2();
      case 2: return renderPhase3();
      case 3: return renderPhase4();
      case 4: return renderPhase5();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Sidebar - Steps */}
      <div className="w-80 bg-background-surface border-r border-border p-8">
        <div className="mb-12">
          <h2 className="text-lg font-heading font-medium text-gold">Compliance Intake</h2>
          <p className="text-sm text-text-secondary mt-1">5 phases to your Regulatory Universe</p>
        </div>

        <div className="space-y-4">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`p-4 rounded-sm border transition-all ${
                index === currentStep
                  ? 'border-gold bg-gold/5'
                  : index < currentStep
                  ? 'border-border-light bg-background-surface'
                  : 'border-border bg-background'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    index === currentStep
                      ? 'bg-gold text-gold-foreground'
                      : index < currentStep
                      ? 'bg-background-surface border border-gold text-gold'
                      : 'bg-background border border-border text-text-tertiary'
                  }`}
                >
                  {index < currentStep ? <Check size={18} weight="bold" /> : step.icon}
                </div>
                <div>
                  <div className={`font-medium ${index <= currentStep ? 'text-text-primary' : 'text-text-tertiary'}`}>
                    {step.title}
                  </div>
                  <div className="text-xs text-text-tertiary">{step.subtitle}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Content */}
      <div className="flex-1 p-12 overflow-y-auto">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
              Phase {currentStep + 1} of {steps.length}
            </div>
            <h1 className="text-3xl font-heading font-medium text-text-primary mb-2">
              {steps[currentStep].title}
            </h1>
            <p className="text-text-secondary">{steps[currentStep].subtitle}</p>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-12 pt-8 border-t border-border">
            <button
              data-testid="intake-back-btn"
              onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)}
              disabled={currentStep === 0}
              className="px-6 py-3 bg-background-surface border border-border text-text-primary rounded-sm hover:border-gold transition-all disabled:opacity-30 disabled:hover:border-border flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              Back
            </button>

            {currentStep < steps.length - 1 ? (
              <button
                data-testid="intake-next-btn"
                onClick={() => canProceed() && setCurrentStep(currentStep + 1)}
                disabled={!canProceed()}
                className="px-6 py-3 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50 flex items-center gap-2"
              >
                Next
                <ArrowRight size={20} />
              </button>
            ) : (
              <button
                data-testid="intake-submit-btn"
                onClick={handleSubmit}
                disabled={loading || !canProceed()}
                className="px-8 py-3 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? 'Generating...' : 'Generate Universe'}
                {!loading && <Check size={20} weight="bold" />}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntakeWizard;
